import { Badge, Card, StatTile } from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { useCashDrawer } from '@/features/billing/api';
import { formatRM } from '@/features/billing/money';
import { useBranch } from '@/features/branch/branch-context';
import {
  useAppointments,
  useQueue,
  useQueueSummary,
} from '@/features/queue/api';
import { klDate, klTime, todayKl } from '@/pages/calendar/time';
import { QueueBoard } from '@/pages/queue/QueueBoard';
import styles from './dashboards.module.css';

/**
 * Front desk dashboard (Admin v2 #7): waiting now (walk-ins vs app
 * check-ins), booked today with an arrived-so-far bar, average wait against
 * yesterday, the live queue, today's bookings and the load by hour. Cash in
 * the drawer belongs to the cash-drawer module (RL-725).
 */
export function FrontDeskDashboard() {
  const { selected: branch } = useBranch();
  const { data: s } = useQueueSummary(!!branch);
  const { data: appts } = useAppointments(todayKl(), 1);
  const { data: queue } = useQueue();
  const today = todayKl();
  const booked = (appts?.appointments ?? []).filter(
    (a) => klDate(a.startsAt) === today && a.status !== 'cancelled',
  );
  const diff =
    s?.averageWaitMinutes != null && s.averageWaitYesterdayMinutes != null
      ? s.averageWaitMinutes - s.averageWaitYesterdayMinutes
      : null;
  // Arrivals per hour, today.
  const hours = new Map<number, number>();
  for (const t of queue?.tickets ?? []) {
    const h = Number(klTime(t.arrivedAt).slice(0, 2));
    hours.set(h, (hours.get(h) ?? 0) + 1);
  }
  const span = Array.from({ length: 14 }, (_, i) => i + 8);
  const peak = Math.max(1, ...span.map((h) => hours.get(h) ?? 0));

  if (!branch)
    return (
      <p className={styles.warnNote}>
        Choose Bachang or Sungai Udang in the branch switch.
      </p>
    );

  return (
    <div className={styles.stack}>
      <div className={styles.tiles}>
        <StatTile
          highlight
          label="Waiting now"
          value={s?.waiting ?? '—'}
          caption={
            s
              ? `${s.waitingWalkIns} walk-ins · ${s.waitingBooked} app check-ins`
              : undefined
          }
        />
        <StatTile
          label="Booked today"
          value={s?.bookedToday ?? '—'}
          caption={
            s ? (
              <span className={styles.barRow}>
                <span
                  className={styles.bar}
                  role="meter"
                  aria-label="Arrived so far"
                  aria-valuemin={0}
                  aria-valuemax={s.bookedToday}
                  aria-valuenow={s.arrivedFromBookings}
                >
                  <span
                    style={{
                      width: `${s.bookedToday ? (100 * s.arrivedFromBookings) / s.bookedToday : 0}%`,
                    }}
                  />
                </span>
                Arrived so far {s.arrivedFromBookings}/{s.bookedToday}
              </span>
            ) : undefined
          }
        />
        <StatTile
          label="Average wait"
          value={
            s?.averageWaitMinutes != null ? `${s.averageWaitMinutes} min` : '—'
          }
          caption={
            diff === null ? (
              'Arrival to called, today'
            ) : (
              <Badge tone={diff <= 0 ? 'success' : 'warning'}>
                {`${diff <= 0 ? '↓' : '↑'} ${Math.abs(diff)} min vs yesterday`}
              </Badge>
            )
          }
        />
        <CashTile />
      </div>
      <div className={styles.split}>
        <QueueBoard title={`Live queue · ${branch.name}`} />
        <Card title="Booked today" actions={<Badge>{branch.name}</Badge>} flush>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Time</th>
                <th>Patient</th>
                <th>
                  <span className="visually-hidden">Status</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {booked.length === 0 && (
                <tr>
                  <td colSpan={3} className={styles.muted}>
                    No bookings today.
                  </td>
                </tr>
              )}
              {booked.map((a) => (
                <tr key={a.id}>
                  <td>{klTime(a.startsAt)}</td>
                  <td>
                    <strong>{a.patient.fullName}</strong>
                    <br />
                    <span className={styles.muted}>{a.service.name}</span>
                  </td>
                  <td>
                    <Badge
                      tone={a.status === 'checked_in' ? 'success' : 'neutral'}
                    >
                      {a.status === 'checked_in'
                        ? a.arrivedLate
                          ? 'Arrived late'
                          : 'Arrived'
                        : a.status === 'no_show'
                          ? 'No-show'
                          : 'Booked'}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
      <Card title={`Today’s load · ${branch.name}`}>
        <div
          className={styles.load}
          role="img"
          aria-label="Arrivals by hour today"
        >
          {span.map((h) => (
            <div key={h} className={styles.loadCol}>
              <span
                className={styles.loadBar}
                style={{ height: `${(100 * (hours.get(h) ?? 0)) / peak}%` }}
                title={`${hours.get(h) ?? 0} arrivals`}
              />
              <span className={styles.muted}>{String(h).padStart(2, '0')}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

/** "Cash in drawer": what should be in the drawer this shift (RL-725). */
function CashTile() {
  const { hasPermission } = useSession();
  const { data } = useCashDrawer(hasPermission('cash_drawer:read'));
  const s = data?.current;
  return (
    <StatTile
      label="Cash in drawer"
      value={s?.expectedSen != null ? formatRM(s.expectedSen) : '—'}
      caption={
        s
          ? `Float ${formatRM(s.floatSen)} · cash taken ${formatRM(s.cashTakenSen)}`
          : 'No shift open'
      }
    />
  );
}
